import { FilterOperator, LogicalOperator, LogicalOperatorUtils } from '@/types/enums';

import type { TaskUtils } from './types';

type FilterCheckCondition = {
  left?: unknown;
  operator?: string;
  right?: unknown;
  field?: unknown;
  value?: unknown;
};

type FilterCheckGroup = {
  operator?: string;
  conditions?: FilterCheckNode[];
};

type FilterCheckNode = FilterCheckGroup | FilterCheckCondition;

function isFilterCheckGroup(node: FilterCheckNode): node is FilterCheckGroup {
  return 'conditions' in node && Array.isArray((node as FilterCheckGroup).conditions);
}

function evaluateGroup(group: FilterCheckGroup): boolean {
  const operator = LogicalOperatorUtils.isValid(group.operator)
    ? group.operator
    : LogicalOperator.And;
  const conditions = group.conditions ?? [];

  if (conditions.length === 0) {
    return false;
  }

  const results = conditions.map((node) =>
    isFilterCheckGroup(node) ? evaluateGroup(node) : evaluateCondition(node)
  );

  switch (operator) {
    case LogicalOperator.Or:
      return results.some(Boolean);
    case LogicalOperator.Nor:
      return results.every((result) => !result);
    case LogicalOperator.And:
    default:
      return results.every(Boolean);
  }
}

function evaluateCondition(condition: FilterCheckCondition): boolean {
  const hasLeft = Object.prototype.hasOwnProperty.call(condition, 'left');
  const hasRight = Object.prototype.hasOwnProperty.call(condition, 'right');

  if (!hasLeft && !hasRight) {
    return false;
  }

  const left = condition.left ?? null;
  const right = condition.right ?? null;

  switch (condition.operator) {
    case FilterOperator.Equal:
      return left === right;
    case FilterOperator.NotEqual:
      return left !== right;
    case FilterOperator.GreaterThan:
      return (
        left !== null && right !== null && (left as string | number) > (right as string | number)
      );
    case FilterOperator.GreaterThanOrEqual:
      return (
        left !== null && right !== null && (left as string | number) >= (right as string | number)
      );
    case FilterOperator.LessThan:
      return (
        left !== null && right !== null && (left as string | number) < (right as string | number)
      );
    case FilterOperator.LessThanOrEqual:
      return (
        left !== null && right !== null && (left as string | number) <= (right as string | number)
      );
    case FilterOperator.In:
      return Array.isArray(right) && right.includes(left);
    case FilterOperator.NotIn:
      return Array.isArray(right) && !right.includes(left);
    case FilterOperator.Contains:
      return (
        typeof left === 'string' &&
        typeof right === 'string' &&
        left.toLowerCase().includes(right.toLowerCase())
      );
    case FilterOperator.StartsWith:
      return typeof left === 'string' && typeof right === 'string' && left.startsWith(right);
    case FilterOperator.EndsWith:
      return typeof left === 'string' && typeof right === 'string' && left.endsWith(right);
    case FilterOperator.IsNull:
      return left === null;
    case FilterOperator.IsNotNull:
      return left !== null;
    case FilterOperator.HasValue:
      return left !== null;
    case FilterOperator.MissingValue:
      return left === null;
    case FilterOperator.Between:
      return (
        Array.isArray(right) &&
        right.length === 2 &&
        (left as string | number) >= (right[0] as string | number) &&
        (left as string | number) <= (right[1] as string | number)
      );
    case FilterOperator.Regex:
      return matchesRegex(left, right);
    default:
      return false;
  }
}

function matchesRegex(left: unknown, right: unknown): boolean {
  if (typeof left !== 'string') {
    return false;
  }

  const pattern =
    typeof right === 'object' && right !== null
      ? String((right as Record<string, unknown>).pattern ?? (right as unknown[])[0] ?? '')
      : String(right ?? '');
  const flags =
    typeof right === 'object' && right !== null
      ? String((right as Record<string, unknown>).flags ?? (right as unknown[])[1] ?? '')
      : '';

  if (pattern === '') {
    return false;
  }

  try {
    return new RegExp(pattern, flags.replace(/[^dgimsuvy]/g, '')).test(left);
  } catch {
    return false;
  }
}

export function evaluateFilter(filters: unknown): boolean {
  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
    return false;
  }

  return evaluateGroup(filters as FilterCheckGroup);
}

function resolvePath(input: unknown, path: string): unknown {
  if (input === null || input === undefined) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(input as object, path)) {
    return (input as Record<string, unknown>)[path];
  }

  return path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc !== null && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined,
      input
    );
}

function resolveConditionNode(node: unknown, input: unknown): unknown {
  if (!node || typeof node !== 'object') {
    return node;
  }

  const obj = node as Record<string, unknown>;

  if (Array.isArray(obj.conditions)) {
    return {
      ...obj,
      conditions: obj.conditions.map((child) => resolveConditionNode(child, input))
    };
  }

  if (typeof obj.field === 'string') {
    return {
      ...obj,
      left: resolvePath(input, obj.field) ?? null,
      right: obj.value ?? null
    };
  }

  if (typeof obj.left !== 'string') {
    return obj;
  }

  return { ...obj, left: resolvePath(input, obj.left) ?? null };
}

export function filterCheck(input: unknown, predicate: unknown): boolean {
  return evaluateFilter(resolveConditionNode(predicate ?? null, input));
}

export const guardUtils: TaskUtils['guard'] = {
  filterCheck: (input, predicate) => filterCheck(input, predicate)
};
